/**
 * Undoable tile removal (UX review). The ✕ and the kebab's "Remove tile" live
 * in `TileFrame`, but `DashboardGrid` owns the removal callback — so the grid
 * hands the doomed tile DEFINITION here, and the toast's Undo re-adds that
 * exact object through the EXISTING `dashboardStore.addTile` action: same id,
 * same config, same layout, so the tile returns to its own cell instead of
 * being appended at the bottom the way "Duplicate" would.
 *
 * No store changes were needed for this — removal and restore are both plain
 * repository mutations the store already exposes.
 */
import { toast } from '@/components/ui/toaster';
import type { DashboardTile } from '../types/dashboardTypes';
import { useDashboardStore } from '../store/dashboardStore';

/** Long enough to actually notice a mis-click and hit Undo (toast default is 5s). */
const UNDO_TOAST_MS = 9000;

/** Fire-and-forget analytics — this runs from a toast callback, not a render
 * (same guard as `useAnalytics`: no-op without an electronAPI). */
const track = (event: string, props: Record<string, string>) => {
  if (typeof window === 'undefined') return;
  window.electronAPI?.analytics?.track(event, props).catch(() => {});
};

export function restoreTile(dashboardId: string, tile: DashboardTile): void {
  track('feature:dashboard_tile_remove_undo', { tileType: tile.tileType });
  void useDashboardStore
    .getState()
    .addTile(dashboardId, tile)
    .then((result) => {
      if (!result.ok) {
        toast.error({ title: 'Could not restore the tile', description: result.detail });
      }
    });
}

/**
 * Runs `remove`, then offers an Undo toast that restores the captured tile.
 * `remove` is the caller's own removal path (the grid's `onRemoveTile`), so
 * runtime-state cleanup and analytics stay where they already live.
 */
export function removeTileWithUndo(
  dashboardId: string,
  tile: DashboardTile,
  remove: (tileId: string) => void,
): void {
  remove(tile.id);
  toast.default({
    title: 'Tile removed',
    description: tile.title,
    duration: UNDO_TOAST_MS,
    testId: 'tile-removed-toast',
    action: { label: 'Undo', onClick: () => restoreTile(dashboardId, tile) },
  });
}
